/**
 * Zero iniziale nei campi numerici (16/10/2026, call Ideariso 12/10).
 *
 * Molti campi quantità partono da 0 (`<input type="number" value={0}>`). In Chrome il cursore resta dopo lo 0 e
 * scrivendo 350 compare «0350»: React non riscrive il campo perché il numero (350) è già quello giusto, e lo 0
 * va cancellato a mano. Vale per i 142 campi numerici del magazzino (entrata, uscita, trasformazione, distinta
 * base) e per quelli del resto del CRM, quindi si sistema una volta sola, a livello di pagina:
 *
 * - al focus il contenuto si seleziona, così il primo tasto lo sostituisce, come già succede entrando col Tab
 *   (anche col clic: il mouseup che seguirebbe toglierebbe la selezione e viene annullato una volta). Vale per
 *   ogni valore, non solo lo 0: in trasformazione e distinta base i campi partono da 1 e cliccando si otteneva
 *   «1350» invece di 350 (verifica Ideariso 28/10). Un secondo clic mette il cursore dove serve;
 * - se comunque si forma uno zero iniziale («0350», «-07») si toglie mentre si scrive. «0,5» e «0.5» restano;
 * - un numero a metà («1.» col punto) non arriva al modulo, che lo leggeva come 0 e trasformava «1.5» in 5.
 *
 * Solo `type="number"`: i codici con zeri iniziali (CAP, codici articolo) sono campi di testo e non si toccano.
 */

/** Valore senza zeri iniziali davanti ad altre cifre: «0350» → «350», «-07» → «-7», «00» → «0»; «0.5» invariato. */
export function stripLeadingZeros(value: string): string {
  return value.replace(/^(-?)0+(?=\d)/, '$1');
}

function isNumberInput(target: EventTarget | null): target is HTMLInputElement {
  return target instanceof HTMLInputElement && target.type === 'number' && !target.readOnly && !target.disabled;
}

// Il setter del prototipo, non quello dell'elemento: React tiene traccia dell'ultimo valore scritto con il
// setter dell'elemento e, se lo usassimo, non vedrebbe il cambio e non chiamerebbe onChange.
let installed = false;

export function installNumberInputZeroFix(doc: Document = document): void {
  if (installed || typeof HTMLInputElement === 'undefined') return;
  const nativeValueSetter = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value')?.set;
  if (!nativeValueSetter) return;
  installed = true;

  let keepSelectionOn: HTMLInputElement | null = null;

  doc.addEventListener('focusin', (event) => {
    const input = event.target;
    if (!isNumberInput(input) || input.value === '') return;
    input.select();
    keepSelectionOn = input;
  });

  doc.addEventListener('mouseup', (event) => {
    if (keepSelectionOn && event.target === keepSelectionOn) event.preventDefault();
    keepSelectionOn = null;
  }, true);

  doc.addEventListener('keydown', () => { keepSelectionOn = null; }, true);

  // Fase di cattura sul documento: arriva prima del gestore di React (sulla radice dell'app), che legge già il
  // valore corretto.
  doc.addEventListener('input', (event) => {
    const input = event.target;
    if (!isNumberInput(input)) return;
    // Numero a metà («1.» col punto): Chrome dà un valore vuoto, il modulo lo leggeva come 0 e riscriveva il
    // campo, e «1.5» diventava 5. Finché il numero è incompleto il modulo non riceve niente; al tasto dopo
    // («1.5») sì.
    if (input.validity.badInput) { event.stopImmediatePropagation(); return; }
    const cleaned = stripLeadingZeros(input.value);
    if (cleaned !== input.value) nativeValueSetter.call(input, cleaned);
  }, true);
}
